import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Copy, RefreshCw, X } from 'lucide-react';
import { errorLogger } from '@/utils/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
  dismissed: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    copied: false,
    dismissed: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error using pino
    errorLogger.error({
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    }, 'Uncaught React error');

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleCopy = () => {
    const { error, errorInfo } = this.state;
    const errorText = `
Error: ${error?.message || 'Unknown error'}

Stack Trace:
${error?.stack || 'No stack trace available'}

Component Stack:
${errorInfo?.componentStack || 'No component stack available'}

User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
    `.trim();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorText)
        .then(() => {
          this.setState({ copied: true });
          setTimeout(() => this.setState({ copied: false }), 3000);
        })
        .catch(() => {
          // Fallback for older browsers
          this.fallbackCopy(errorText);
        });
    } else {
      this.fallbackCopy(errorText);
    }
  };

  private fallbackCopy = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 3000);
    } catch (err) {
      errorLogger.error({ err }, 'Failed to copy error to clipboard');
    }
    document.body.removeChild(textArea);
  };

  private handleDismiss = () => {
    errorLogger.info('User dismissed error boundary');
    this.setState({
      dismissed: true,
    });

    // Attempt to recover without reload after a short delay
    setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        copied: false,
        dismissed: false,
      });
    }, 300);
  };

  private handleReset = () => {
    errorLogger.info('User requested app reload');
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
      dismissed: false,
    });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError && !this.state.dismissed) {
      const { error, errorInfo, copied } = this.state;

      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 p-4 flex items-center justify-center">
          <Card className="max-w-2xl w-full shadow-xl border-red-200 dark:border-red-900 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <CardHeader className="border-b border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div>
                    <CardTitle className="text-red-900 dark:text-red-100">
                      Oops! Something went wrong
                    </CardTitle>
                    <CardDescription className="text-red-700 dark:text-red-300">
                      The app encountered an unexpected error
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={this.handleDismiss}
                  className="text-red-600 hover:text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40"
                  aria-label="Dismiss error"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Error Message:
                </h3>
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4">
                  <code className="text-sm text-red-800 dark:text-red-200 break-words">
                    {error?.message || 'Unknown error occurred'}
                  </code>
                </div>
              </div>

              {error?.stack && (
                <details className="space-y-2">
                  <summary className="text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    Stack Trace (click to expand)
                  </summary>
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-48 overflow-y-auto mt-2">
                    <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words font-mono">
                      {error.stack}
                    </pre>
                  </div>
                </details>
              )}

              {errorInfo?.componentStack && (
                <details className="space-y-2">
                  <summary className="text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    Component Stack (click to expand)
                  </summary>
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-32 overflow-y-auto mt-2">
                    <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words font-mono">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={this.handleCopy}
                  variant="outline"
                  className="flex-1"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {copied ? 'Copied!' : 'Copy Error Details'}
                </Button>
                <Button
                  onClick={this.handleDismiss}
                  variant="secondary"
                  className="flex-1"
                >
                  <X className="mr-2 h-4 w-4" />
                  Try to Continue
                </Button>
                <Button
                  onClick={this.handleReset}
                  variant="default"
                  className="flex-1"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reload App
                </Button>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong>Tip:</strong> Copy the error details for support. You can try to continue,
                  or reload the app for a fresh start.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
